/**
 * What to say about my location sharing, from the sharing state + the setting + the mission.
 * Pure (no React) so the words are unit-tested. Sharing is automatic; the UI only needs a tiny
 * status (dot on the location button, one line in Settings) and a hint when something is wrong.
 */
import type { Id } from '@apos/shared';
import { formatAccuracy, REFERENCE_MAX_ACCURACY_M, type SharingState } from '../../lib/geolocation';

/** Problems worth a (small, dismissible) hint. Everything else stays quiet. */
export type ShareIssue = 'blocked' | 'unsupported' | 'low-accuracy';

export type ShareTone = 'live' | 'aging' | 'lost' | 'neutral';

export interface SharingView {
  /** Actively sharing (or getting the first fix) for this mission. */
  on: boolean;
  /** One-line status, e.g. "Sharing · ±8 m", "Off", "Blocked". */
  text: string;
  tone: ShareTone;
  issue: ShareIssue | null;
  /** Current accuracy when sharing with a fix. */
  accuracyM: number | null;
}

export interface SharingContext {
  missionId: Id | null;
  /** prefs.shareLocation */
  enabled: boolean;
  /** The mission is live. */
  live: boolean;
  /** My role may share (crew and up). */
  canShare: boolean;
}

export function sharingView(s: SharingState, c: SharingContext): SharingView {
  const quiet = (text: string, tone: ShareTone = 'neutral'): SharingView => ({ on: false, text, tone, issue: null, accuracyM: null });
  if (!c.missionId) return quiet('Off');
  if (!c.canShare) return quiet('Viewers don’t share location');
  if (!c.enabled) return quiet('Off');
  const mine = s.missionId === c.missionId;
  if (mine && s.status === 'denied') return { on: false, text: 'Blocked — location is off for Airview Studio', tone: 'lost', issue: 'blocked', accuracyM: null };
  if (mine && s.status === 'unsupported') return { on: false, text: 'Unavailable on this device', tone: 'lost', issue: 'unsupported', accuracyM: null };
  if (!c.live) return quiet('On · starts when the mission is live');
  if (mine && (s.status === 'active' || s.status === 'requesting')) {
    const acc = s.lastFix?.accuracyM ?? null;
    const queued = s.queued ? ` · ${s.queued} queued` : '';
    if (s.status === 'requesting' || !s.lastFix) return { on: true, text: `Sharing · locating…${queued}`, tone: 'aging', issue: null, accuracyM: null };
    if (acc != null && acc > REFERENCE_MAX_ACCURACY_M) {
      return { on: true, text: `Sharing · ${formatAccuracy(acc)} LOW ACCURACY${queued}`, tone: 'aging', issue: 'low-accuracy', accuracyM: acc };
    }
    return { on: true, text: `Sharing${acc != null ? ` · ${formatAccuracy(acc)}` : ''}${queued}`, tone: 'live', issue: null, accuracyM: acc };
  }
  if (mine && s.status === 'error') return quiet('Reconnecting…', 'aging');
  return quiet('Starting…');
}
